/**
 * =============================================================================
 * PeriodFilterBar — Barra de filtro por período para o painel do afiliado.
 *
 * Visual clean e moderno seguindo o padrão de listagens (estilo financeiro):
 *   - Pílulas à esquerda com presets rápidos: Hoje · Ontem · Este mês ·
 *     Personalizado.
 *   - À direita, quando o preset é "Este mês", reaproveita o `MonthNavigator`
 *     já consagrado (setas + label) para navegar entre meses.
 *   - "Personalizado" abre um Popover com `Calendar mode="range"` (Shadcn) e
 *     botão "Aplicar".
 *   - Botão `X` reseta para o estado padrão (Este mês = mês atual).
 *
 * O componente é totalmente controlado: recebe `value` e dispara `onChange`.
 * O helper exportado `resolvePeriod()` converte o estado em um intervalo
 * `{ start: Date, end: Date }` (end exclusivo) pronto para enviar à RPC.
 *
 * @version 1.0.0
 * RELEASE: 2026-04-29 v1.0.0 - Criação do componente unificado de período.
 * =============================================================================
 */
import { useMemo, useState } from 'react';
import { CalendarIcon } from 'lucide-react';
import {
  startOfDay,
  endOfDay,
  startOfMonth,
  endOfMonth,
  addDays,
  format,
} from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { MonthNavigator } from '@/components/financial/MonthNavigator';
import { cn } from '@/lib/utils';

/** Presets disponíveis. */
export type PeriodPreset = 'today' | 'yesterday' | 'month' | 'custom';

/** Estado controlado do filtro. */
export interface PeriodFilterValue {
  preset: PeriodPreset;
  /** Mês de referência quando preset = 'month'. Default: hoje. */
  month: Date;
  /** Intervalo customizado quando preset = 'custom'. */
  range?: { from: Date; to: Date };
}

/** Valor inicial padrão (mês atual). */
export const DEFAULT_PERIOD: PeriodFilterValue = {
  preset: 'month',
  month: new Date(),
};

/**
 * Resolve o estado para um intervalo concreto `{ start, end }` (end exclusivo).
 * Útil para enviar ao backend como `p_period_start` / `p_period_end`.
 */
export function resolvePeriod(value: PeriodFilterValue): {
  start: Date;
  end: Date;
} {
  const now = new Date();
  switch (value.preset) {
    case 'today': {
      const s = startOfDay(now);
      return { start: s, end: addDays(s, 1) };
    }
    case 'yesterday': {
      const s = startOfDay(addDays(now, -1));
      return { start: s, end: addDays(s, 1) };
    }
    case 'custom': {
      if (value.range?.from && value.range?.to) {
        return {
          start: startOfDay(value.range.from),
          end: addDays(startOfDay(value.range.to), 1),
        };
      }
      // Fallback: mês atual
      return {
        start: startOfMonth(now),
        end: addDays(endOfMonth(now), 1),
      };
    }
    case 'month':
    default: {
      const ref = value.month ?? now;
      return {
        start: startOfMonth(ref),
        end: addDays(endOfMonth(ref), 1),
      };
    }
  }
}

interface PeriodFilterBarProps {
  value: PeriodFilterValue;
  onChange: (next: PeriodFilterValue) => void;
  className?: string;
}

/**
 * Barra de filtro por período (presets + mês + intervalo customizado).
 */
export function PeriodFilterBar({
  value,
  onChange,
  className,
}: PeriodFilterBarProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  // Estado temporário do range dentro do Popover (só aplica ao clicar Aplicar)
  const [tempRange, setTempRange] = useState<
    { from?: Date; to?: Date } | undefined
  >(value.range);

  /** Aplica preset rápido. */
  const setPreset = (preset: PeriodPreset) => {
    if (preset === 'custom') {
      setPopoverOpen(true);
      return;
    }
    onChange({ ...value, preset, month: preset === 'month' ? new Date() : value.month });
  };

  /** Confirma intervalo customizado. */
  const applyCustomRange = () => {
    if (tempRange?.from && tempRange?.to) {
      onChange({
        ...value,
        preset: 'custom',
        range: { from: tempRange.from, to: tempRange.to },
      });
      setPopoverOpen(false);
    }
  };

  /** Reseta para o padrão (mês atual). */
  const handleClear = () => onChange(DEFAULT_PERIOD);

  /** Label resumido do filtro custom para o botão. */
  const customLabel = useMemo(() => {
    if (value.preset === 'custom' && value.range?.from && value.range?.to) {
      const from = format(value.range.from, 'dd/MM', { locale: ptBR });
      const to = format(value.range.to, 'dd/MM', { locale: ptBR });
      return `${from} – ${to}`;
    }
    return 'Personalizado';
  }, [value]);

  /** Definição das pílulas. */
  const pills: { id: PeriodPreset; label: string }[] = [
    { id: 'today', label: 'Hoje' },
    { id: 'yesterday', label: 'Ontem' },
    { id: 'month', label: 'Este mês' },
  ];

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2 justify-between',
        className,
      )}
    >
      {/* ----- Lado esquerdo: navegador de mês (com setinhas) -----
          Vem PRIMEIRO para ficar centralizado/à esquerda da página.
          No mobile ocupa a linha inteira centralizado; em telas maiores
          fica colado à esquerda como âncora visual do filtro. */}
      {value.preset === 'month' ? (
        <div className="w-full flex justify-center sm:w-auto sm:justify-start">
          <MonthNavigator
            currentMonth={value.month}
            onChange={(m) => onChange({ ...value, preset: 'month', month: m })}
            onClear={handleClear}
          />
        </div>
      ) : (
        // Placeholder vazio para preservar o `justify-between` mesmo quando
        // o navegador de mês não está visível (presets today/yesterday/custom).
        <div />
      )}

      {/* ----- Lado direito: pílulas de preset rápidas ----- */}
      <div className="flex flex-nowrap items-center gap-1">
        {pills.map((p) => {
          const active = value.preset === p.id;
          return (
            <Button
              key={p.id}
              type="button"
              size="sm"
              variant={active ? 'default' : 'outline'}
              onClick={() => setPreset(p.id)}
              className={cn(
                'h-6 rounded-full px-2.5 text-[11px] font-semibold whitespace-nowrap shrink-0 transition-all',
                !active && 'bg-background hover:bg-muted/60',
              )}
            >
              {p.label}
            </Button>
          );
        })}

        {/* ----- Personalizado (Popover com range) ----- */}
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              size="sm"
              variant={value.preset === 'custom' ? 'default' : 'outline'}
              className={cn(
                'h-6 rounded-full px-2.5 text-[11px] font-semibold whitespace-nowrap shrink-0 gap-1',
                value.preset !== 'custom' && 'bg-background hover:bg-muted/60',
              )}
            >
              <CalendarIcon className="h-3 w-3" />
              {customLabel}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <Calendar
              mode="range"
              selected={tempRange as any}
              onSelect={(r: any) => setTempRange(r)}
              numberOfMonths={1}
              initialFocus
              locale={ptBR}
              className={cn('p-3 pointer-events-auto')}
            />
            <div className="flex items-center justify-end gap-2 border-t p-2">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  setTempRange(undefined);
                  setPopoverOpen(false);
                }}
              >
                Cancelar
              </Button>
              <Button
                size="sm"
                onClick={applyCustomRange}
                disabled={!tempRange?.from || !tempRange?.to}
              >
                Aplicar
              </Button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
